import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { StaffMember, AuditLogItem, StaffRole, StaffStatus } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import {
  Shield,
  UserPlus,
  Users,
  History,
  CheckCircle,
  AlertTriangle,
  Lock,
  Trash2,
  RefreshCw,
  Server,
  Cloud,
  Key,
  Eye,
  EyeOff,
  Check,
  ShieldCheck,
  Copy,
  Sparkles,
  Clock,
  Link2,
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { getCurrentOrigin } from '../lib/domain';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

export const SettingsPage: React.FC = () => {
  const { showToast } = useToast();
  const { user, permissions } = useAuth();

  const [activeTab, setActiveTab] = useState<'security' | 'staff' | 'audit' | 'system'>('security');
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Add staff modal
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<StaffRole>('marketing_staff');
  const [isSavingStaff, setIsSavingStaff] = useState(false);

  // Delete staff modal
  const [deleteStaffTarget, setDeleteStaffTarget] = useState<StaffMember | null>(null);

  // Invite link modal state
  const [inviteModalData, setInviteModalData] = useState<{
    staffName: string;
    staffEmail: string;
    inviteUrl: string;
    isNew: boolean;
  } | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [generatingInviteId, setGeneratingInviteId] = useState<string | null>(null);

  const fetchStaff = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{ items: StaffMember[] }>('/staff');
      setStaffList(data.items || []);
    } catch (err: any) {
      if (err.status !== 403) {
        showToast('Staff Load Error', err.message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{ items: AuditLogItem[] }>('/audit-logs');
      setAuditLogs(data.items || []);
    } catch (err: any) {
      showToast('Audit Log Error', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'staff') fetchStaff();
    if (activeTab === 'audit') fetchAuditLogs();
  }, [activeTab]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      showToast('Validation Error', 'Please enter your current password.', 'error');
      return;
    }
    if (newPassword.length < 8) {
      showToast('Validation Error', 'New password must be at least 8 characters long.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Validation Error', 'New passwords do not match.', 'error');
      return;
    }

    try {
      setIsChangingPassword(true);
      const res = await api.post<{ success: boolean; message: string }>('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });

      showToast('Password Updated', res.message || 'Password updated successfully.', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      showToast('Password Change Failed', err.message || 'Could not update password.', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim()) {
      showToast('Missing Fields', 'Email and name are required', 'error');
      return;
    }

    try {
      setIsSavingStaff(true);
      const res = await api.post<{
        staff: StaffMember;
        invite?: { inviteUrl?: string; token: string };
      }>('/staff', {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
      });

      const fullInviteUrl =
        res.invite?.inviteUrl || `${window.location.origin}/setup-password?token=${res.invite?.token}`;

      showToast('Staff Authorized', `${newName} has been added to the allowlist`, 'success');
      setIsAddStaffOpen(false);
      setNewEmail('');
      setNewName('');
      fetchStaff();

      setInviteModalData({
        staffName: res.staff?.name || newName,
        staffEmail: res.staff?.email || newEmail,
        inviteUrl: fullInviteUrl,
        isNew: true,
      });
    } catch (err: any) {
      showToast('Failed to Add Staff', err.message, 'error');
    } finally {
      setIsSavingStaff(false);
    }
  };

  const handleGenerateInvite = async (member: StaffMember) => {
    try {
      setGeneratingInviteId(member.id);
      const res = await api.post<{
        success: boolean;
        invite: { inviteUrl?: string; token: string };
      }>(`/staff/${member.id}/invite`);

      const fullInviteUrl =
        res.invite?.inviteUrl || `${window.location.origin}/setup-password?token=${res.invite?.token}`;

      setInviteModalData({
        staffName: member.name,
        staffEmail: member.email,
        inviteUrl: fullInviteUrl,
        isNew: false,
      });
      showToast('Invite Link Ready', `Invitation link generated for ${member.name}`, 'info');
    } catch (err: any) {
      showToast('Invite Generation Failed', err.message, 'error');
    } finally {
      setGeneratingInviteId(null);
    }
  };

  const copyInviteToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      showToast('Link Copied', 'Invitation URL copied to clipboard', 'success');
      setTimeout(() => setIsCopied(false), 2500);
    } catch {
      showToast('Copy Failed', 'Please manually select and copy the link', 'error');
    }
  };

  const handleUpdateRole = async (staffId: string, role: StaffRole, status: StaffStatus) => {
    try {
      await api.patch(`/staff/${staffId}`, { role, status });
      showToast('Staff Updated', 'Permissions updated successfully', 'success');
      fetchStaff();
    } catch (err: any) {
      showToast('Update Failed', err.message, 'error');
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteStaffTarget) return;
    try {
      await api.delete(`/staff/${deleteStaffTarget.id}`);
      showToast('Staff Removed', `${deleteStaffTarget.email} removed from allowlist`, 'info');
      setDeleteStaffTarget(null);
      fetchStaff();
    } catch (err: any) {
      showToast('Removal Failed', err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-white tracking-tight">
          System Settings & Governance
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          Manage your account security, authorized personnel, role-based governance, and audit logs.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800 overflow-x-auto">
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
            activeTab === 'security'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          id="tab-security"
        >
          <Lock className="w-4 h-4" />
          <span>Security & Password</span>
        </button>

        <button
          onClick={() => setActiveTab('staff')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
            activeTab === 'staff'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          id="tab-staff"
        >
          <Users className="w-4 h-4" />
          <span>Staff Allowlist</span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
            activeTab === 'audit'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          id="tab-audit"
        >
          <History className="w-4 h-4" />
          <span>Audit Trail</span>
        </button>

        <button
          onClick={() => setActiveTab('system')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
            activeTab === 'system'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
          id="tab-system"
        >
          <Server className="w-4 h-4" />
          <span>Infrastructure</span>
        </button>
      </div>

      {/* Tab: Security & Password */}
      {activeTab === 'security' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Password Change Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center text-orange-400 shadow-sm">
                    <Key className="w-4 h-4" />
                  </div>
                  <div>
                    <CardTitle>Change Password</CardTitle>
                    <CardDescription>
                      Update your login password to secure your Brototype Link Manager account.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <form onSubmit={handleChangePassword} className="space-y-4 pt-2">
                {/* Current Password */}
                <div className="space-y-1.5">
                  <label htmlFor="current-password" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Current Password <span className="text-orange-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter your current password (e.g. Brototype@2026)"
                      required
                      id="current-password"
                      className="w-full px-3.5 py-2.5 bg-slate-900/90 text-slate-100 placeholder:text-slate-500 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 focus:ring-1 focus:ring-orange-500/50 text-sm transition-all pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1"
                      tabIndex={-1}
                      title={showCurrentPassword ? 'Hide password' : 'Show password'}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    New Password <span className="text-orange-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                      id="new-password"
                      className="w-full px-3.5 py-2.5 bg-slate-900/90 text-slate-100 placeholder:text-slate-500 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 focus:ring-1 focus:ring-orange-500/50 text-sm transition-all pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1"
                      tabIndex={-1}
                      title={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password requirements hint */}
                  <div className="flex items-center gap-4 pt-1 text-[11px]">
                    <span className={`flex items-center gap-1 font-medium ${newPassword.length >= 8 ? 'text-emerald-400' : 'text-slate-500'}`}>
                      <Check className="w-3 h-3" /> 8+ characters
                    </span>
                    <span className={`flex items-center gap-1 font-medium ${/[0-9!@#$%^&*]/.test(newPassword) ? 'text-emerald-400' : 'text-slate-500'}`}>
                      <Check className="w-3 h-3" /> Number or symbol
                    </span>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-1.5">
                  <label htmlFor="confirm-new-password" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                    Confirm New Password <span className="text-orange-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      required
                      id="confirm-new-password"
                      className={`w-full px-3.5 py-2.5 bg-slate-900/90 text-slate-100 placeholder:text-slate-500 border rounded-xl focus:outline-none text-sm transition-all pr-10 ${
                        confirmPassword && confirmPassword !== newPassword
                          ? 'border-rose-500 focus:border-rose-500 focus:ring-1 focus:ring-rose-500/50'
                          : 'border-slate-700 focus:border-orange-500/80 focus:ring-1 focus:ring-orange-500/50'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1"
                      tabIndex={-1}
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-rose-400 font-medium pt-1">
                      Passwords do not match.
                    </p>
                  )}
                </div>

                {/* Action button */}
                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isChangingPassword}
                    disabled={!currentPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                    leftIcon={<Lock className="w-4 h-4" />}
                    id="submit-change-password-btn"
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          {/* Side Info Cards */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div>
                  <CardTitle className="text-sm">Account Overview</CardTitle>
                  <CardDescription>Your current staff profile</CardDescription>
                </div>
              </CardHeader>
              <div className="space-y-3 text-xs">
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                  <span className="text-slate-400 text-[11px] uppercase tracking-wider font-semibold">Email</span>
                  <p className="font-mono text-slate-200 text-xs font-semibold">{user?.email || 'marketing@brototype.com'}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                  <span className="text-slate-400 text-[11px] uppercase tracking-wider font-semibold">Name</span>
                  <p className="text-slate-200 text-xs font-semibold">{user?.name || 'Staff Member'}</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-slate-400 text-[11px] uppercase tracking-wider font-semibold">Role</span>
                  <Badge variant={user?.role === 'admin' ? 'primary' : 'neutral'}>
                    {user?.role ? user.role.replace('_', ' ') : 'Staff'}
                  </Badge>
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <CardTitle className="text-sm">Session Revocation Policy</CardTitle>
                </div>
              </CardHeader>
              <div className="text-xs text-slate-400 space-y-2.5 leading-relaxed">
                <p>
                  When you update your password, your current browser session remains safely active while all other active sessions across devices are immediately revoked.
                </p>
                <p>
                  Passwords are encrypted with PBKDF2 using SHA-256 and 100,000 hash iterations with unique per-user salts.
                </p>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Tab: Staff Allowlist */}
      {activeTab === 'staff' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Only employees with emails listed below can log in via Cloudflare Access.
            </p>

            {permissions.canManageStaff && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsAddStaffOpen(true)}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                Add Staff Member
              </Button>
            )}
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Staff Member</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Added On</th>
                    {permissions.canManageStaff && <th className="py-3 px-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {staffList.map((member) => (
                    <tr key={member.id} className="hover:bg-slate-900/40">
                      <td className="py-3 px-4">
                        <p className="font-semibold text-slate-200">{member.name}</p>
                        <p className="text-slate-400 font-mono text-[11px]">{member.email}</p>
                      </td>

                      <td className="py-3 px-4">
                        {permissions.canManageStaff && member.id !== user?.id ? (
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleUpdateRole(member.id, e.target.value as StaffRole, member.status)
                            }
                            className="bg-slate-900 text-slate-200 border border-slate-700 rounded-lg px-2 py-1 text-xs focus:outline-none cursor-pointer"
                          >
                            <option value="marketing_staff">Marketing Staff</option>
                            <option value="admin">Administrator</option>
                          </select>
                        ) : (
                          <Badge variant={member.role === 'admin' ? 'primary' : 'neutral'}>
                            {member.role.replace('_', ' ')}
                          </Badge>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1 items-start">
                          <Badge variant={member.status === 'active' ? 'active' : 'disabled'}>
                            {member.status}
                          </Badge>
                          {member.has_password ? (
                            <span className="text-[10px] text-emerald-400 flex items-center gap-1 font-mono">
                              <Check className="w-3 h-3" /> Password Active
                            </span>
                          ) : (
                            <span className="text-[10px] text-amber-400/90 flex items-center gap-1 font-mono">
                              <Clock className="w-3 h-3" /> Invite Pending
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">
                        {formatDate(member.created_at)}
                      </td>

                      {permissions.canManageStaff && (
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleGenerateInvite(member)}
                              disabled={generatingInviteId === member.id}
                              className="px-2 py-1 rounded-lg text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 transition-colors cursor-pointer flex items-center gap-1.5 text-[11px]"
                              title="Generate / Copy Setup Link"
                            >
                              <Link2 className="w-3 h-3 text-orange-400" />
                              <span>{generatingInviteId === member.id ? 'Generating...' : 'Invite Link'}</span>
                            </button>

                            {member.id !== user?.id ? (
                              <button
                                onClick={() => setDeleteStaffTarget(member)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors cursor-pointer"
                                title="Remove staff member"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <span className="text-slate-600 text-[10px] pl-1">You</span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Audit Trail */}
      {activeTab === 'audit' && (
        <Card className="p-0 overflow-hidden">
          <CardHeader className="p-4 border-b border-slate-800">
            <div>
              <CardTitle>Administrative Action Logs</CardTitle>
              <CardDescription>
                Immutable record of all link creations, destination edits, status changes, and staff updates
              </CardDescription>
            </div>
          </CardHeader>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                  <th className="py-3 px-4">Timestamp</th>
                  <th className="py-3 px-4">Actor</th>
                  <th className="py-3 px-4">Action</th>
                  <th className="py-3 px-4">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {auditLogs.length > 0 ? (
                  auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-900/40 font-mono text-[11px]">
                      <td className="py-3 px-4 text-slate-400 whitespace-nowrap">
                        {formatDate(log.created_at, true)}
                      </td>
                      <td className="py-3 px-4 text-orange-400 font-semibold">{log.actor_email}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200">
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-300 font-sans text-xs">
                        {log.change_summary || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-slate-500">
                      No audit events recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab: System & Infrastructure */}
      {activeTab === 'system' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="w-4 h-4 text-orange-400" />
                  <span>Cloudflare Production Configuration</span>
                </CardTitle>
                <CardDescription>Zero-server edge deployment status</CardDescription>
              </div>
            </CardHeader>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Active Domain (Dynamic)</span>
                <span className="font-mono font-bold text-orange-400">{getCurrentOrigin() || 'Auto-detected'}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Database Engine</span>
                <span className="font-mono text-emerald-400">Cloudflare D1 (SQLite)</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Authentication Layer</span>
                <span className="font-mono text-emerald-400">Cloudflare Access (JWT)</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Redirect Engine Status</span>
                <span className="font-mono text-emerald-400">HTTP 302 Native Worker</span>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-orange-400" />
                  <span>Security & Boundary Enforcement</span>
                </CardTitle>
                <CardDescription>Policy guidelines for Brototype operations</CardDescription>
              </div>
            </CardHeader>

            <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
              <p>
                &bull; <strong>Server-side claim verification:</strong> All management APIs verify the <code className="text-orange-400 font-mono">Cf-Access-Jwt-Assertion</code> cryptographic signature from Cloudflare Access.
              </p>
              <p>
                &bull; <strong>Public redirect isolation:</strong> Public visitors on <code className="text-orange-400 font-mono">/:shortCode</code> never require authentication and execute in under 10ms.
              </p>
              <p>
                &bull; <strong>Non-PII click tracking:</strong> IP addresses and intrusive fingerprints are never stored in D1 to maintain strict compliance and privacy.
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Add Staff Member Modal */}
      <Modal
        isOpen={isAddStaffOpen}
        onClose={() => setIsAddStaffOpen(false)}
        title="Add Staff Member to Allowlist"
        description="Grants dashboard and API access to authorized Brototype staff"
        maxWidth="md"
      >
        <form onSubmit={handleAddStaff} className="space-y-4">
          <Input
            label="Corporate Email Address"
            required
            type="email"
            placeholder="colleague@brototype.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />

          <Input
            label="Full Name"
            required
            placeholder="e.g. John Doe"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
              Role & Permissions
            </label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as StaffRole)}
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
            >
              <option value="marketing_staff">Marketing Staff (Create & edit links, campaigns, analytics)</option>
              <option value="admin">Administrator (Full control, manage staff permissions, delete links)</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-800">
            <Button type="button" variant="ghost" size="sm" onClick={() => setIsAddStaffOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" isLoading={isSavingStaff}>
              Authorize Employee
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Staff Confirmation Modal */}
      {deleteStaffTarget && (
        <Modal
          isOpen={!!deleteStaffTarget}
          onClose={() => setDeleteStaffTarget(null)}
          title="Remove Staff Access"
          description={`Revoking access for: ${deleteStaffTarget.email}`}
          maxWidth="sm"
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              This will remove {deleteStaffTarget.name} from the active staff allowlist. They will no longer be able to log in to the Link Manager dashboard.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setDeleteStaffTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDeleteStaff}>
                Revoke Access
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Staff Invitation Link Modal */}
      {inviteModalData && (
        <Modal
          isOpen={!!inviteModalData}
          onClose={() => setInviteModalData(null)}
          title={inviteModalData.isNew ? 'Staff Member Authorized' : 'Staff Invitation Link'}
          description={`One-time password setup link for ${inviteModalData.staffName}`}
          maxWidth="md"
        >
          <div className="space-y-4">
            <div className="p-3.5 rounded-xl bg-orange-500/10 border border-orange-500/30 text-xs text-orange-300 flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                <strong>Share this setup link with {inviteModalData.staffName}:</strong>
                <p className="text-[11px] text-orange-200/80 mt-0.5">
                  When they open this link, they will create their password and will be signed into the dashboard automatically. The link remains valid for 7 days.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Invitation Setup URL
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteModalData.inviteUrl}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="flex-1 px-3 py-2 text-xs bg-slate-950 font-mono text-slate-200 border border-slate-800 rounded-xl focus:outline-none focus:border-orange-500/80 select-all"
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => copyInviteToClipboard(inviteModalData.inviteUrl)}
                  className="shrink-0"
                >
                  {isCopied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-white" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Link</span>
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-800 text-xs">
              <span className="text-slate-400 text-[11px]">
                Target account: <span className="font-mono text-slate-300">{inviteModalData.staffEmail}</span>
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setInviteModalData(null)}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
